import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  Stack,
  LinearProgress,
  InputAdornment,
  alpha,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsCar as TruckIcon,
  PersonRemove as UnassignIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Badge as BadgeIcon,
  Search as SearchIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { elevatedPanelSx } from '../../constants/shape';
import { useTruckStore } from '../../stores/transportation.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { useAuthStore } from '../../stores/auth.store';
import { TruckSchema } from '../../types/transportation.type';
import { UserSchema } from '../../types/auth.type';
import * as transportationService from '../../services/transportation.service';
import DriverFormDialog, {
  type DriverCreateFormState,
  type DriverProfileFormState,
} from '../../components/transportation/DriverFormDialog';
import DriverViewDialog, {
  type DriverViewModel,
} from '../../components/transportation/DriverViewDialog';
import AssignTruckDialog from '../../components/transportation/AssignTruckDialog';
import { OrganizationType } from '../../types/organization.type';
import { userHasAnyLegacyRole } from '../../utils/permissions.helpers';
import { MIN_DRIVER_PASSWORD_LENGTH, isValidDriverPassword } from '../../utils/driverPassword';
import { isSuccessfulDriverActionResponse } from './drivers.helpers';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAUDI_PHONE_REGEX = /^(\+9665|9665|05)[0-9]{8}$/;

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test((value || '').trim());
}

function isValidSaudiPhone(value: string): boolean {
  const normalized = (value || '').trim().replace(/\s/g, '');
  return normalized ? SAUDI_PHONE_REGEX.test(normalized) : true;
}

interface DriverWithTruck extends DriverViewModel {}

function getDriverName(driver: DriverWithTruck): string {
  const first = (driver.data as { first_name?: string })?.first_name?.trim();
  const last = (driver.data as { last_name?: string })?.last_name?.trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) return fullName;
  return (
    driver.username ||
    driver.phone ||
    (driver.data as { phone?: string })?.phone ||
    'â€”'
  );
}

function getDriverSubtitle(driver: DriverWithTruck): string | null {
  const first = (driver.data as { first_name?: string })?.first_name?.trim();
  const last = (driver.data as { last_name?: string })?.last_name?.trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  const email = driver.username?.trim();
  if (fullName && email) return email;
  return null;
}

const Drivers: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganizationStore();
  const { trucks, fetchTrucks } = useTruckStore();
  const { getOrganizationUsers } = useAuthStore();
  const { canMutate } = useAuthPermissions();
  const orgId = selectedOrganization?.id;
  const canCreateDriver = Boolean(orgId && canMutate(`recyclee.${orgId}.*.driver.register`));
  const canEditDriver = Boolean(orgId && canMutate(`recyclee.${orgId}.*.user.write`));
  const canAssignDriver = Boolean(orgId && canMutate(`recyclee.${orgId}.*.truck.assign`));
  const [viewDriver, setViewDriver] = useState<DriverWithTruck | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [drivers, setDrivers] = useState<DriverWithTruck[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<DriverWithTruck | null>(null);
  const [search, setSearch] = useState('');
  /** Knowledge Base "Go to Add driver" guided tips. */
  type HelpAddPhase = 'addButton' | 'form' | 'submit';
  const [helpAddPhase, setHelpAddPhase] = useState<HelpAddPhase | null>(null);
  /** Knowledge Base "Go to View driver details" tip. */
  const [helpViewHint, setHelpViewHint] = useState(false);
  /** Knowledge Base "Go to Edit & assign" row-action tips. */
  type HelpEditAssignPhase = 'edit' | 'assign';
  const [helpEditAssignPhase, setHelpEditAssignPhase] =
    useState<HelpEditAssignPhase | null>(null);
  const {
    isStep,
    tourPhase,
    completeStep,
    dismissTour,
    setTourPhase,
    advanceTourPhase,
  } = useSelfSetupTour();
  const tourFiltersHint = isStep('drivers') && tourPhase === 'filters';
  const tourTableHint = isStep('drivers') && tourPhase === 'table';
  const tourAddHint = isStep('drivers') && tourPhase === 'addButton';
  const tourFormHint =
    (isStep('drivers') && tourPhase === 'form') || helpAddPhase === 'form';
  const tourSubmitHint =
    (isStep('drivers') && tourPhase === 'submit') || helpAddPhase === 'submit';
  const tourDialogOpen = tourFormHint || tourSubmitHint;
  const [dismissAddHint, setDismissAddHint] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState<DriverCreateFormState>({
    username: '',
    password: '',
    password_confirm: '',
    truck_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
    nationality: '',
    date_of_birth: '',
    iban: '',
    driver_license_expiry: '',
    driver_license_number: '',
    vehicle_registration_number: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editFormData, setEditFormData] = useState<DriverProfileFormState>({
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
    nationality: '',
    date_of_birth: '',
    iban: '',
    driver_license_expiry: '',
    driver_license_number: '',
    vehicle_registration_number: '',
  });
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});

  // Separate state for assign truck dialog
  const [assignTruckId, setAssignTruckId] = useState('');

  // Check if organization type is valid (recycler or truck)
  useEffect(() => {
    // Wait for organization to be selected before checking
    if (!selectedOrganization) {
      return;
    }

    // Check if organization type is recycler or truck
    const isValidOrgType = 
      selectedOrganization.organization_type === OrganizationType.RECYCLER ||
      selectedOrganization.organization_type === OrganizationType.TRUCK;

    // Only redirect if organization type is invalid
    if (!isValidOrgType) {
      navigate('/pickup_orders');
    }
  }, [selectedOrganization, navigate]);

  // Load drivers and trucks
  useEffect(() => {
    if (selectedOrganization?.id) {
      loadDrivers();
      fetchTrucks({ organization_id: selectedOrganization.id });
    }
  }, [selectedOrganization]);

  const loadDrivers = async () => {
    if (!selectedOrganization?.id) return;

    setLoading(true);
    try {
      const allUsers = await getOrganizationUsers(selectedOrganization.id);
      
      // Filter users with the org-type-appropriate driver/staff role.
      // Role names live in data.permissions_legacy (data.permissions is the
      // flat granular-permission array, not a role map) — see
      // userHasAnyLegacyRole.
      const driverUsers = allUsers.filter((u: UserSchema) =>
        userHasAnyLegacyRole(u, ['truck_co_staff', 'recycler_staff'])
      );

      // Fetch assigned truck for each driver
      const driversWithTrucks = await Promise.all(
        driverUsers.map(async (driver: UserSchema) => {
          // Only try to fetch truck if driver has a valid ID
          const driverId = driver.sub || (driver as any).id;
          if (!driverId) {
            return {
              ...driver,
              assignedTruck: null,
            };
          }

          try {
            const response = await transportationService.getDriverTruck(driverId);
            // Handle both response formats: { success, data } or { result }
            const data = response as { success?: boolean; data?: TruckSchema; result?: TruckSchema };
            if (data.success && data.data) {
              return {
                ...driver,
                assignedTruck: data.data,
              };
            } else if (data.result) {
              return {
                ...driver,
                assignedTruck: data.result,
              };
            }
            return {
              ...driver,
              assignedTruck: null,
            };
          } catch (error) {
            // Driver has no truck assigned (404 is expected)
            return {
              ...driver,
              assignedTruck: null,
            };
          }
        })
      );

      setDrivers(driversWithTrucks);
    } catch (error) {
      showSnackbar(t('transportation.drivers.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'success'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setViewDriver(null);
  };
  const handleOpen = () => {
    if (selectedOrganization?.id && trucks.length === 0) {
      fetchTrucks({ organization_id: selectedOrganization.id });
    }
    setFormData({
      username: '',
      password: '',
      password_confirm: '',
      truck_id: '',
      first_name: '',
      last_name: '',
      phone: '',
      gender: '',
      nationality: '',
      date_of_birth: '',
      iban: '',
      driver_license_expiry: '',
      driver_license_number: '',
      vehicle_registration_number: '',
    });
    setFormErrors({});
    setOpen(true);
  };

  useEffect(() => {
    const state = location.state as {
      openAdd?: boolean;
      showAddHint?: boolean;
      showDriverViewHint?: boolean;
      showDriverEditAssignHint?: boolean;
    } | null;
    if (
      !state?.openAdd &&
      !state?.showAddHint &&
      !state?.showDriverViewHint &&
      !state?.showDriverEditAssignHint
    ) {
      return;
    }
    if (state.openAdd) handleOpen();
    if (state.showAddHint) {
      setHelpAddPhase('addButton');
      setDismissAddHint(false);
    }
    if (state.showDriverViewHint) {
      setSearch('');
      setHelpViewHint(true);
      setHelpEditAssignPhase(null);
    }
    if (state.showDriverEditAssignHint) {
      setSearch('');
      setHelpEditAssignPhase('edit');
      setHelpViewHint(false);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const resetCreateForm = () => {
    setFormData({
      username: '',
      password: '',
      password_confirm: '',
      truck_id: '',
      first_name: '',
      last_name: '',
      phone: '',
      gender: '',
      nationality: '',
      date_of_birth: '',
      iban: '',
      driver_license_expiry: '',
      driver_license_number: '',
      vehicle_registration_number: '',
    });
    setFormErrors({});
  };

  const finishHelpAddGuide = () => {
    setHelpAddPhase(null);
    setOpen(false);
    resetCreateForm();
  };

  const handleClose = () => {
    if (helpAddPhase) {
      finishHelpAddGuide();
      return;
    }
    if (
      (isStep('drivers') && tourPhase === 'form') ||
      (isStep('drivers') && tourPhase === 'submit')
    ) {
      dismissTour();
    }
    setOpen(false);
    resetCreateForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganization?.id) {
      showSnackbar(t('transportation.drivers.selectOrganization'), 'error');
      return;
    }

    const err: Record<string, string> = {};
    const hasEmail = !!formData.username.trim();
    const hasPhone = !!formData.phone.trim();
    if (!hasEmail && !hasPhone) {
      err.username = t('transportation.drivers.validation.emailOrPhoneRequired');
      err.phone = t('transportation.drivers.validation.emailOrPhoneRequired');
    }
    if (formData.username.trim() && !isValidEmail(formData.username)) {
      err.username = t('transportation.drivers.validation.invalidEmail');
    }
    if (formData.phone.trim() && !isValidSaudiPhone(formData.phone)) {
      err.phone = t('transportation.drivers.validation.invalidPhone');
    }
    if (!formData.password) {
      err.password = t('transportation.drivers.validation.passwordRequired');
    } else if (!isValidDriverPassword(formData.password)) {
      err.password = t('transportation.drivers.validation.passwordTooShort', {
        min: MIN_DRIVER_PASSWORD_LENGTH,
      });
    }
    if (formData.password !== formData.password_confirm) err.password_confirm = t('transportation.drivers.validation.passwordsDoNotMatch');
    if (!formData.driver_license_number) err.driver_license_number = t('transportation.drivers.validation.licenseRequired');
    if (!formData.vehicle_registration_number) err.vehicle_registration_number = t('transportation.drivers.validation.registrationRequired');
    setFormErrors(err);
    if (Object.keys(err).length > 0) return;

    try {
      setLoading(true);
      const response = await transportationService.registerDriver({
        ...(formData.username.trim() ? { username: formData.username.trim() } : {}),
        password: formData.password,
        password_confirm: formData.password_confirm,
        organization_id: selectedOrganization.id,
        truck_id: formData.truck_id || null,
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        phone: formData.phone.trim() || null,
        gender: formData.gender.trim() || null,
        nationality: formData.nationality.trim() || null,
        date_of_birth: formData.date_of_birth.trim() || null,
        iban: formData.iban.trim() || null,
        driver_license_expiry: formData.driver_license_expiry.trim() || null,
        driver_license_number: formData.driver_license_number.trim(),
        vehicle_registration_number: formData.vehicle_registration_number.trim(),
      });

      if (isSuccessfulDriverActionResponse(response)) {
        showSnackbar(t('transportation.drivers.driverCreated'), 'success');
        handleClose();
        loadDrivers();
        if (formData.truck_id) {
          fetchTrucks({ organization_id: selectedOrganization.id });
        }
      } else {
        showSnackbar(t('transportation.drivers.failedToCreate'), 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || t('transportation.drivers.failedToCreate');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDriver = (driver?: DriverWithTruck) => {
    const d = driver ?? selectedDriver;
    if (!d) return;

    setEditFormData({
      first_name: (d.data as any)?.first_name ?? '',
      last_name: (d.data as any)?.last_name ?? '',
      phone: (d as any).phone ?? (d.data as any)?.phone ?? '',
      gender: (d.data as any)?.gender ?? '',
      nationality: (d.data as any)?.nationality ?? '',
      date_of_birth: (d.data as any)?.date_of_birth ?? '',
      iban: (d.data as any)?.iban ?? '',
      driver_license_expiry: (d.data as any)?.driver_license_expiry ?? '',
      driver_license_number: (d.data as any)?.driver_license_number ?? '',
      vehicle_registration_number: (d.data as any)?.vehicle_registration_number ?? '',
    });
    setEditFormErrors({});
    setSelectedDriver(d);
    setEditDialogOpen(true);
  };

  const handleViewDriver = (driver: DriverWithTruck) => {
    setViewDriver(driver);
    setViewDialogOpen(true);
  };

  const handleAssignTruck = (driver: DriverWithTruck) => {
    if (selectedOrganization?.id && trucks.length === 0) {
      fetchTrucks({ organization_id: selectedOrganization.id });
    }
    setSelectedDriver(driver);
    setAssignTruckId('');
    setAssignDialogOpen(true);
  };

  const handleUnassignTruck = async (driver: DriverWithTruck) => {
    if (!driver.assignedTruck?.id) {
      showSnackbar(t('transportation.drivers.noAssignedTruck'), 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await transportationService.unassignDriverFromTruck(
        driver.assignedTruck.id
      );
      if (isSuccessfulDriverActionResponse(response)) {
        showSnackbar(t('transportation.drivers.unassignedSuccess'), 'success');
        loadDrivers();
        fetchTrucks({ organization_id: selectedOrganization!.id });
      } else {
        showSnackbar(t('transportation.drivers.failedToUnassign'), 'error');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      const errorMessage = err.response?.data?.detail || t('transportation.drivers.failedToUnassign');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedDriver || !assignTruckId) {
      showSnackbar(t('transportation.drivers.selectTruckError'), 'error');
      return;
    }

    const driverId = selectedDriver.sub || (selectedDriver as any).id;
    if (!driverId) {
      showSnackbar(t('transportation.drivers.driverIdNotFound'), 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await transportationService.assignDriverToTruck(assignTruckId, {
        driver_id: driverId,
      });
      // Handle both response formats: { success, data } or { result }
      if (isSuccessfulDriverActionResponse(response)) {
        showSnackbar(t('transportation.drivers.assignedSuccess'), 'success');
        setAssignDialogOpen(false);
        setAssignTruckId('');
        setSelectedDriver(null);
        loadDrivers();
        fetchTrucks({ organization_id: selectedOrganization!.id });
      } else {
        showSnackbar(t('transportation.drivers.failedToAssign'), 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || t('transportation.drivers.failedToAssign');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDriver || !selectedOrganization?.id) {
      showSnackbar(t('transportation.drivers.driverOrOrgNotFound'), 'error');
      return;
    }

    const err: Record<string, string> = {};
    if (editFormData.phone && !isValidSaudiPhone(editFormData.phone)) err.phone = t('transportation.drivers.validation.invalidPhone');
    if (!editFormData.driver_license_number) err.driver_license_number = t('transportation.drivers.validation.licenseRequired');
    if (!editFormData.vehicle_registration_number) err.vehicle_registration_number = t('transportation.drivers.validation.registrationRequired');
    setEditFormErrors(err);
    if (Object.keys(err).length > 0) return;

    const driverId = selectedDriver.sub || (selectedDriver as any).id;
    if (!driverId) {
      showSnackbar(t('transportation.drivers.driverIdNotFound'), 'error');
      return;
    }

    try {
      setLoading(true);
      const { updateOrganizationUser } = useAuthStore.getState();
      const result = await updateOrganizationUser(selectedOrganization.id, driverId, {
        phone: editFormData.phone.trim() || null,
        data: {
          ...(selectedDriver.data || {}),
          first_name: editFormData.first_name.trim() || null,
          last_name: editFormData.last_name.trim() || null,
          gender: editFormData.gender || null,
          nationality: editFormData.nationality || null,
          date_of_birth: editFormData.date_of_birth || null,
          iban: editFormData.iban.trim() || null,
          driver_license_expiry: editFormData.driver_license_expiry || null,
          driver_license_number: editFormData.driver_license_number.trim(),
          vehicle_registration_number: editFormData.vehicle_registration_number.trim(),
        },
      });

      if (result) {
        showSnackbar(t('transportation.drivers.driverUpdated'), 'success');
        setEditDialogOpen(false);
        setSelectedDriver(null);
        loadDrivers();
      } else {
        showSnackbar(t('transportation.drivers.failedToUpdate'), 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || t('transportation.drivers.failedToUpdate');
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const availableTrucks = useMemo(() => {
    return trucks.filter(truck => 
      !truck.driver_id || 
      truck.driver_id === null || 
      truck.driver_id === undefined ||
      truck.driver_id === ''
    );
  }, [trucks]);

  const filteredDrivers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return drivers;

    return drivers.filter((driver) => {
      const haystack = [
        getDriverName(driver),
        getDriverSubtitle(driver),
        driver.username,
        driver.phone,
        (driver.data as { phone?: string })?.phone,
        (driver.data as { driver_license_number?: string })?.driver_license_number,
        (driver.data as { vehicle_registration_number?: string })?.vehicle_registration_number,
        driver.assignedTruck?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [drivers, search]);

  const colCount = 5;

  // Self-setup: open create dialog once when entering form/submit tip phases.
  // Must stay above early returns — hooks cannot be conditional.
  useEffect(() => {
    if (!tourDialogOpen) return;
    if (open) return;
    handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when entering dialog tip phases
  }, [tourDialogOpen]);

  const showAddHint =
    !dismissAddHint &&
    !loading &&
    !open &&
    (tourAddHint ||
      (helpAddPhase === 'addButton' &&
        !(
          isStep('drivers') &&
          (tourPhase === 'nav' ||
            tourPhase === 'filters' ||
            tourPhase === 'table' ||
            tourPhase === 'form' ||
            tourPhase === 'submit')
        )));

  if (!selectedOrganization) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          {t('transportation.drivers.selectOrganization')}
        </Typography>
      </Box>
    );
  }

  if (loading && drivers.length === 0) {
    return <LoadingSkeleton variant="table" filters={false} columns={5} rows={6} />;
  }

  return (
    <Box>
      <PageHeader
        icon={<BadgeIcon />}
        title={t('transportation.drivers.title')}
        actions={
          canCreateDriver ? (
          <SetupCoachmark
            open={showAddHint}
            title={t('transportation.drivers.addDriverHintTitle')}
            subtitle={t('transportation.drivers.addDriverHint')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              setDismissAddHint(true);
              if (tourAddHint) {
                advanceTourPhase();
                return;
              }
              if (helpAddPhase === 'addButton') {
                setHelpAddPhase('form');
                handleOpen();
              }
            }}
            onDismiss={() => {
              setDismissAddHint(true);
              if (tourAddHint) dismissTour();
              else if (helpAddPhase === 'addButton') setHelpAddPhase(null);
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDismissAddHint(true);
                handleOpen();
                if (tourAddHint) setTourPhase('form');
                else if (helpAddPhase === 'addButton') setHelpAddPhase('form');
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('transportation.drivers.addDriver')}
            </Button>
          </SetupCoachmark>
          ) : undefined
        }
      />

      <SetupCoachmark
        open={tourFiltersHint}
        fullWidth
        title={t('selfSetup.tour.driversFiltersTitle')}
        subtitle={t('selfSetup.tour.driversFiltersSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Card sx={{ mb: 2 }} elevation={0} variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('transportation.drivers.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 360,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
          </CardContent>
        </Card>
      </SetupCoachmark>

      <SetupCoachmark
        open={tourTableHint}
        fullWidth
        title={t('selfSetup.tour.driversTableTitle')}
        subtitle={t('selfSetup.tour.driversTableSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Paper elevation={2} sx={elevatedPanelSx}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table
            stickyHeader
            sx={{
              '& .MuiTableCell-head': { py: 1.5, px: 2 },
              '& .MuiTableCell-body': { py: 1.75, px: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('transportation.drivers.driverLicense')}</TableCell>
                <TableCell>{t('transportation.drivers.vehicleRegistration')}</TableCell>
                <TableCell>{t('transportation.drivers.assignedTruck')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ py: 8, border: 0 }}>
                    <SetupCoachmark
                      open={(helpViewHint || Boolean(helpEditAssignPhase)) && !loading}
                      fullWidth
                      title={t('transportation.drivers.rowActionsEmptyTitle')}
                      subtitle={t('transportation.drivers.rowActionsEmptyHint')}
                      nextLabel={t('selfSetup.tourDone')}
                      onNext={() => {
                        setHelpViewHint(false);
                        setHelpEditAssignPhase(null);
                      }}
                      onDismiss={() => {
                        setHelpViewHint(false);
                        setHelpEditAssignPhase(null);
                      }}
                    >
                      <Stack alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.text.secondary, 0.08),
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <InboxIcon fontSize="large" />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {search.trim()
                            ? t('transportation.drivers.noSearchResults')
                            : t('transportation.drivers.noDrivers')}
                        </Typography>
                      </Stack>
                    </SetupCoachmark>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrivers.map((driver, rowIndex) => {
                  const license = (driver.data as { driver_license_number?: string })
                    ?.driver_license_number;
                  const registration = (driver.data as { vehicle_registration_number?: string })
                    ?.vehicle_registration_number;
                  const subtitle = getDriverSubtitle(driver);
                  const isHelpTargetRow =
                    rowIndex === 0 && (helpViewHint || Boolean(helpEditAssignPhase));

                  return (
                    <TableRow
                      key={driver.sub || (driver as { id?: string }).id || driver.username}
                      hover
                      sx={{ transition: 'background-color 0.2s ease' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {getDriverName(driver)}
                        </Typography>
                        {subtitle ? (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {subtitle}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: license ? 'text.primary' : 'text.disabled',
                          }}
                          noWrap
                        >
                          {license || 'â€”'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: registration ? 'text.primary' : 'text.disabled',
                          }}
                          noWrap
                        >
                          {registration || 'â€”'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {driver.assignedTruck ? (
                          <Chip
                            size="small"
                            label={driver.assignedTruck.name}
                            sx={{
                              height: 26,
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('transportation.drivers.notAssigned')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {isHelpTargetRow && helpViewHint ? (
                          <SetupCoachmark
                            open
                            title={t('transportation.drivers.viewDriverHintTitle')}
                            subtitle={t('transportation.drivers.viewDriverHint')}
                            nextLabel={t('selfSetup.tourDone')}
                            onNext={() => setHelpViewHint(false)}
                            onDismiss={() => setHelpViewHint(false)}
                          >
                            <Tooltip title={t('common.viewDetails')}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDriver(driver)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </SetupCoachmark>
                        ) : (
                          <Tooltip title={t('common.viewDetails')}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDriver(driver)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEditDriver && (isHelpTargetRow && helpEditAssignPhase === 'edit' ? (
                          <SetupCoachmark
                            open
                            title={t('transportation.drivers.editDriverHintTitle')}
                            subtitle={t('transportation.drivers.editDriverHint')}
                            nextLabel={t('selfSetup.tourNext')}
                            onNext={() => setHelpEditAssignPhase('assign')}
                            onDismiss={() => setHelpEditAssignPhase(null)}
                          >
                            <Tooltip title={t('common.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditDriver(driver)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </SetupCoachmark>
                        ) : (
                          <Tooltip title={t('common.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditDriver(driver)}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        {canAssignDriver && (isHelpTargetRow && helpEditAssignPhase === 'assign' ? (
                          <SetupCoachmark
                            open
                            title={t('transportation.drivers.assignDriverHintTitle')}
                            subtitle={t('transportation.drivers.assignDriverHint')}
                            nextLabel={t('selfSetup.tourDone')}
                            onNext={() => setHelpEditAssignPhase(null)}
                            onDismiss={() => setHelpEditAssignPhase(null)}
                          >
                            <Tooltip title={t('transportation.drivers.assignToTruck')}>
                              <IconButton
                                size="small"
                                onClick={() => handleAssignTruck(driver)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <TruckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </SetupCoachmark>
                        ) : (
                          <Tooltip title={t('transportation.drivers.assignToTruck')}>
                            <IconButton
                              size="small"
                              onClick={() => handleAssignTruck(driver)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <TruckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        {canAssignDriver && driver.assignedTruck ? (
                          <Tooltip title={t('transportation.drivers.unassignFromTruck')}>
                            <IconButton
                              size="small"
                              onClick={() => handleUnassignTruck(driver)}
                              sx={{ color: theme.palette.warning.main }}
                            >
                              <UnassignIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </SetupCoachmark>

      <DriverViewDialog
        open={viewDialogOpen}
        onClose={handleCloseView}
        driver={viewDriver}
        onEdit={canEditDriver ? (driver) => {
          handleCloseView();
          handleEditDriver(driver);
        } : undefined}
      />

      {/* Create Driver Dialog */}
      <DriverFormDialog
        mode="create"
        open={open}
        onClose={handleClose}
        loading={loading}
        canSubmit={canCreateDriver}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        availableTrucks={availableTrucks}
        tourFormHint={tourFormHint}
        tourSubmitHint={tourSubmitHint}
        onTourFormNext={() => {
          if (helpAddPhase === 'form') {
            setHelpAddPhase('submit');
            return;
          }
          advanceTourPhase();
        }}
        onTourSubmitDone={() => {
          if (helpAddPhase === 'submit') {
            finishHelpAddGuide();
            return;
          }
          completeStep('drivers');
          setOpen(false);
          setFormErrors({});
        }}
        onTourDismiss={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          dismissTour();
          setOpen(false);
          setFormErrors({});
        }}
      />

      {/* Edit Driver Dialog */}
      <DriverFormDialog
        mode="edit"
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedDriver(null);
        }}
        loading={loading}
        canSubmit={canEditDriver}
        onSubmit={handleEditSubmit}
        formData={editFormData}
        setFormData={setEditFormData}
        formErrors={editFormErrors}
        username={selectedDriver?.username ?? ''}
      />

      <AssignTruckDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setAssignTruckId('');
          setSelectedDriver(null);
        }}
        loading={loading}
        canSubmit={canAssignDriver}
        driver={selectedDriver}
        availableTrucks={availableTrucks}
        truckId={assignTruckId}
        onTruckIdChange={setAssignTruckId}
        onSubmit={handleAssignSubmit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Drivers;

